import type Dexie from 'dexie';

/**
 * **Product migration v003 — `Match.format`, backfilled `'11v11'`.**
 *
 * Design §13.3 and §6.0a, verbatim in effect: *"Adds `format` to the `matches` store, backfilling
 * **`'11v11'`** for every existing row. It touches no other store, needs no `reconcileNeeded` flag
 * (nothing derived changes), adds **no index**, and imports no `domain/`."*
 *
 * ── What it is for, which is a live defect and not a tidy-up ─────────────────
 *
 * PRE-1 asked for a formation picker over a fixed shape list and named no shapes. The design chose
 * eight, and every one of them was an **11v11** shape with ten field players — so the app shipped
 * unable to describe the stakeholder's own daughter's team, which plays **3-3-2**: eight field
 * players, 9v9. Nothing in the requirements said that was wrong and nothing in the build could have
 * caught it; a person opened the picker and recognised shapes his team cannot field (§19 F-40).
 * PRE-8 closes it, and this migration is what lets a device that already holds matches keep them.
 *
 * ── Why the backfill is `'11v11'`, and why that is checkable ─────────────────
 *
 * Every `formationGuess` any existing row can hold was chosen from the one shape list that has ever
 * existed in this app, and that list is the 11v11 list. So after the backfill every stored guess is
 * still offerable by its own match's format, every pre-existing match's picker shows exactly the
 * shapes it showed before, and nothing the user can see changes. Behaviour-preserving **by
 * construction**, the same class of reasoning as `v002`'s `0` sentinel.
 *
 * Unlike `v002`, it is also **checked**: §6.0a's cross-field row-schema refinement refuses a `Match`
 * row whose `formationGuess` its `format` does not offer, so any other backfill value fails the
 * v001 fixture at the repo boundary. `fixtureMigration.test.ts` runs that mutation as a test rather
 * than trusting this paragraph — a migration test that cannot be made to fail by changing the
 * backfill is asserting the column, not the behaviour.
 *
 * ── The literal is spelled here on purpose ──────────────────────────────────
 *
 * `'11v11'` is written out rather than imported from `types/`'s `MATCH_FORMATS`. A migration that
 * named a shared constant would change what it does the day that constant changed, which is the
 * whole reason §13.3 says migrations take plain rows and import no vocabulary. NFR-7 is
 * forward-only: what this wrote in August 2026 must still be what it writes in 2028.
 *
 * ── No `.stores()` call, deliberately ───────────────────────────────────────
 *
 * `format` is never a query key — it is read off a match row already in hand — so an index here
 * would invalidate U-0's measured index set for a lookup nothing performs. Dexie inherits the
 * previous version's schema when `stores()` is omitted.
 */
export function v003MatchFormat(db: Dexie): void {
  db.version(3).upgrade(async (tx) => {
    await tx
      .table('matches')
      .toCollection()
      .modify((row: { format?: string }) => {
        row.format = '11v11';
      });
  });
}
